/**
 * The rail's "Other ways to add" (Option C, C1): the four entry points as quiet rows, placed at the rail's bottom by
 * the dialog's grid but AFTER the footer in the DOM and Tab order (plan §3.6). While a Save is in flight they stay
 * visible but unavailable with "Saving…" (`ReasonButton`); Import opens its source menu from its row.
 * C1b: each row is the flip item of its start-view row (same key, a hidden surface and blurb for the flight).
 */
import { forwardRef, type ComponentProps } from 'react';
import { cn } from '@/lib/utils';
import { ReasonButton } from '@/components/ui/ReasonButton';
import { ImportSourcesMenu } from '../../connection-import';
import type { EntryPointsApi } from '../hooks/useEntryPoints';
import { ENTRY_POINTS, type EntryPointInfo } from '../start/entryPoints';
import { entryOptionId } from '../start/engineFilter';

type Props = { entries: EntryPointsApi; pasteOpen: boolean; className?: string };

type RowProps = ComponentProps<typeof ReasonButton> & { entry: EntryPointInfo };

const RailEntryRow = forwardRef<HTMLButtonElement, RowProps>(function RailEntryRow({ entry, className, ...props }, ref) {
  return (
    <ReasonButton
      ref={ref}
      type="button"
      variant="ghost"
      data-focus-role="entry"
      data-testid={entry.testId}
      data-flip-key={entryOptionId(entry.id)}
      className={cn('relative h-[30px] w-full justify-start gap-2 px-2 text-[13px] font-normal text-muted-foreground hover:text-foreground', className)}
      {...props}
    >
      <span aria-hidden data-flip-layer="surface" className="absolute inset-0 rounded-lg border border-border bg-background opacity-0" />
      <span data-flip-layer="icon" className="relative flex">
        <entry.Icon className="h-[15px] w-[15px]" aria-hidden />
      </span>
      <span data-flip-layer="label" className="relative">
        {entry.label}
      </span>
      <span aria-hidden data-flip-layer="blurb" data-flip-ghost className="absolute left-8 top-full truncate text-left text-xs text-muted-foreground opacity-0">
        {entry.description}
      </span>
    </ReasonButton>
  );
});

export function RailEntryPoints({ entries, pasteOpen, className }: Props) {
  return (
    <div role="group" aria-label="Other ways to add" className={cn('relative flex flex-col gap-px px-2 pb-2 pt-1.5', className)} data-testid="rail-entry-points">
      <span aria-hidden data-flip-chrome="fade" className="absolute inset-x-0 top-0 h-px bg-border" />
      {ENTRY_POINTS.map((entry) => {
        const reason = entries.reasonFor(entry.id);
        if (entry.id === 'import' && !reason) {
          return (
            <ImportSourcesMenu
              key={entry.id}
              entry="connection_dialog"
              placement="rail"
              open={entries.importMenuAt === 'rail'}
              onOpenChange={(open) => (open ? entries.activate('import', 'rail') : entries.closeImportMenu())}
              trigger={<RailEntryRow entry={entry} />}
            />
          );
        }
        return (
          <RailEntryRow
            key={entry.id}
            entry={entry}
            reason={reason}
            aria-expanded={entry.id === 'paste' ? pasteOpen : undefined}
            onClick={() => entries.activate(entry.id, 'rail')}
          />
        );
      })}
    </div>
  );
}
